/**
 * Resizable columns for the cash book capture grid — the pure half.
 *
 * IN `lib/` AND NOT IN THE COMPONENT, for the reason every file here is:
 * `npm test -w web` is node's own runner and node cannot parse JSX. The drag
 * itself (pointer events, the <col> it moves) is in CashBook.jsx and cannot be
 * run by this repository; the clamp, the stored shape and the table width
 * arithmetic are what can, so they live here.
 *
 * WIDTHS ARE PER VIEWER AND LIVE IN localStorage ONLY. They are a screen
 * preference, not a fact about the company, so nothing is sent to the server and
 * nothing here may be load-bearing: every storage touch is wrapped, and a
 * browser with storage blocked simply gets the defaults every time.
 */

export const MIN_COLUMN_WIDTH = 40;
export const MAX_COLUMN_WIDTH = 1200;
export const GRID_WIDTHS_KEY = 'ironledger.cashBook.columnWidths.v1';

/**
 * The grid's columns, left to right: `[key, defaultWidth]`. A null width is the
 * one column that takes whatever is left — Description, deliberately: it is the
 * narration the bookkeeper reads back against the statement. These are the
 * widths that used to sit as inline `style={{width}}` on each <th>.
 */
export const GRID_COLUMNS = [
  ['lineno', 34],
  ['per', 42],
  ['date', 118],
  ['gcs', 56],
  ['account', 190],
  ['reconciled', 30],
  ['reference', 108],
  ['description', null],
  ['tax', 86],
  ['excl', 118],
  ['incl', 118],
  ['actions', 28],
];

/** What Description is given room for in the table's minimum width when it is auto. */
export const AUTO_COLUMN_ALLOWANCE = 220;

/** The columns on screen: the delete column only exists on a batch that is open. */
export const gridColumns = (isOpen) =>
  GRID_COLUMNS.filter(([key]) => isOpen || key !== 'actions');

/** A drag's result, held to the floor and the ceiling and to whole pixels. */
export function clampWidth(width, min = MIN_COLUMN_WIDTH, max = MAX_COLUMN_WIDTH) {
  const n = Number(width);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/**
 * Whatever came out of storage, reduced to `{ key: width }` for known keys only.
 * Anything else — a corrupt string, an old shape, a width that is not a number —
 * is dropped, so a bad entry costs one column its saved width and never the page.
 */
export function sanitiseWidths(raw, columns = GRID_COLUMNS) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [key] of columns) {
    const v = raw[key];
    if (typeof v === 'number' && Number.isFinite(v)) out[key] = clampWidth(v);
  }
  return out;
}

/** The saved widths, or `{}`. Never throws: storage may be blocked or hold junk. */
export function loadWidths(storage, key = GRID_WIDTHS_KEY, columns = GRID_COLUMNS) {
  try {
    const text = storage?.getItem(key);
    return text ? sanitiseWidths(JSON.parse(text), columns) : {};
  } catch { return {}; }
}

/** Persist the widths. Returns whether it stuck; an empty set removes the entry. */
export function saveWidths(storage, widths, key = GRID_WIDTHS_KEY) {
  try {
    if (!storage) return false;
    if (Object.keys(widths ?? {}).length === 0) storage.removeItem(key);
    else storage.setItem(key, JSON.stringify(widths));
    return true;
  } catch { return false; }
}

/** `window.localStorage`, or null where merely touching it throws. */
export function safeStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch { return null; }
}

/** A column's effective width: what the viewer saved, else its default (null = auto). */
export function widthFor(key, saved, columns = GRID_COLUMNS) {
  if (saved && Object.hasOwn(saved, key)) return saved[key];
  const col = columns.find(([k]) => k === key);
  return col ? col[1] : null;
}

/** `{ key: width|null }` for every column on screen. */
export function effectiveWidths(saved, columns = GRID_COLUMNS) {
  return Object.fromEntries(columns.map(([key]) => [key, widthFor(key, saved, columns)]));
}

/**
 * The table's own width, from the effective widths.
 *
 * `table-layout: fixed` takes the sum of its columns as the table's width when
 * that exceeds `width: 100%`, and hands any surplus to the auto column. With an
 * auto Description that is exactly right, so only a minimum is needed (and
 * Description is allowed AUTO_COLUMN_ALLOWANCE in it, so it can never be starved
 * to nothing by the others). Once Description has a width of its own there is no
 * auto column left to absorb a surplus, and the browser would stretch every
 * column instead — so the table is pinned to the exact sum.
 */
export function tableWidthStyle(widths) {
  const values = Object.values(widths);
  const hasAuto = values.some((w) => w === null);
  const sum = values.reduce((s, w) => s + (w ?? 0), 0);
  return hasAuto
    ? { minWidth: sum + AUTO_COLUMN_ALLOWANCE }
    : { width: sum, minWidth: sum };
}

/** Drop one column's saved width (double-click on its handle). */
export function withoutWidth(saved, key) {
  const next = { ...saved };
  delete next[key];
  return next;
}
